/**
 * Expenses Page
 * Track and manage church expenses with approval workflow
 */

import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import {
  Plus,
  Search,
  Filter,
  Download,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  AlertCircle
} from 'lucide-react'
import { AdminUser } from '@/lib/authService'
import { ExpenseService } from '@/services/expenseService'
import { VendorService, type VendorSummary } from '@/services/vendorService'
import type { Expense } from '@/services/financeService'
import { format } from 'date-fns'
import { formatCurrency } from '@/lib/currency'

interface ExpensesProps {
  user: AdminUser | null
}

export default function Expenses({ user }: ExpensesProps) {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [vendors, setVendors] = useState<VendorSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showApprovalDialog, setShowApprovalDialog] = useState(false)
  const [showPaymentDialog, setShowPaymentDialog] = useState(false)
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')

  // Form state
  const [formData, setFormData] = useState({
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    vendor_id: '',
    vendor_name: '',
    description: '',
    expense_category: 'general',
    amount: '',
    vat_amount: '',
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    payment_method: '',
    notes: ''
  })

  // Approval form state
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve')
  const [approvalComments, setApprovalComments] = useState('')

  // Payment form state
  const [paymentData, setPaymentData] = useState({
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'bank_transfer',
    payment_reference: '',
    payment_amount: ''  // Allow partial payments
  })

  useEffect(() => {
    if (user?.churchId) {
      loadExpenses()
      loadVendors()
    }
  }, [user, filterStatus])

  async function loadExpenses() {
    try {
      setLoading(true)
      const result = await ExpenseService.getExpenses(user!.churchId, {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        limit: 50
      })
      setExpenses(result.data)
    } catch (error) {
      console.error('Error loading expenses:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadVendors() {
    try {
      const data = await VendorService.getVendors(user!.churchId, { status: 'active' })
      setVendors(data)
    } catch (error) {
      console.error('Error loading vendors:', error)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      const amount = parseFloat(formData.amount)
      const vatAmount = parseFloat(formData.vat_amount || '0')
      const totalAmount = amount + vatAmount

      await ExpenseService.createExpense(
        {
          church_id: user!.churchId,
          ...formData,
          // Convert empty strings to null for date fields
          invoice_date: formData.invoice_date || null,
          due_date: formData.due_date || null,
          amount,
          vat_amount: vatAmount,
          total_amount: totalAmount,
          currency: 'GHS'
        } as any,
        user!.id
      )

      setShowAddDialog(false)
      loadExpenses()
      resetForm()
    } catch (error) {
      console.error('Error creating expense:', error)
      alert('Failed to create expense')
    }
  }

  async function handleApproval() {
    if (!selectedExpense) return

    try {
      if (approvalAction === 'approve') {
        await ExpenseService.approveExpense(
          selectedExpense.id,
          user!.id,
          approvalComments
        )
        alert('Expense approved successfully')
      } else {
        if (!approvalComments.trim()) {
          alert('Please provide a reason for rejection')
          return
        }
        await ExpenseService.rejectExpense(
          selectedExpense.id,
          user!.id,
          approvalComments
        )
        alert('Expense rejected')
      }

      setShowApprovalDialog(false)
      setSelectedExpense(null)
      setApprovalComments('')
      loadExpenses()
    } catch (error) {
      console.error('Error processing approval:', error)
      alert('Failed to process approval')
    }
  }

  async function handlePayment() {
    if (!selectedExpense) return

    try {
      await ExpenseService.markAsPaid(
        selectedExpense.id,
        paymentData,
        user!.id
      )

      alert('Payment recorded successfully')
      setShowPaymentDialog(false)
      setSelectedExpense(null)
      setPaymentData({
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        payment_method: 'bank_transfer',
        payment_reference: '',
        payment_amount: ''
      })
      loadExpenses()
    } catch (error: any) {
      console.error('Error recording payment:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
      const errorMessage = error?.message || error?.error_description || error?.details || error?.hint || 'Failed to record payment'
      alert(`Failed to record payment: ${errorMessage}`)
    }
  }

  function resetForm() {
    setFormData({
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      vendor_id: '',
      vendor_name: '',
      description: '',
      expense_category: 'general',
      amount: '',
      vat_amount: '',
      invoice_number: '',
      invoice_date: '',
      due_date: '',
      payment_method: '',
      notes: ''
    })
  }

  function getStatusBadge(status: string) {
    const variants: Record<string, { variant: any; icon: any; label: string }> = {
      draft: { variant: 'outline', icon: FileText, label: 'Draft' },
      submitted: { variant: 'secondary', icon: Clock, label: 'Submitted' },
      pending_approval: { variant: 'default', icon: AlertCircle, label: 'Pending' },
      approved: { variant: 'default', icon: CheckCircle2, label: 'Approved' },
      rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
      partially_paid: { variant: 'secondary', icon: DollarSign, label: 'Partial' },
      paid: { variant: 'default', icon: CheckCircle2, label: 'Paid' }
    }

    const config = variants[status] || variants.draft
    const Icon = config.icon

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    )
  }

  const filteredExpenses = expenses.filter(e => {
    const matchesSearch =
      e.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesSearch
  })

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Expenses</h1>
            <p className="text-sm text-muted-foreground">
              Track and manage church expenses
            </p>
          </div>
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by description, vendor, or invoice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="partially_paid">Partially Paid</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No expenses found
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell>
                      {format(new Date(expense.expense_date), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{expense.vendor_name || '-'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate">{expense.description}</div>
                    </TableCell>
                    <TableCell className="capitalize">
                      {expense.expense_category?.replace('_', ' ')}
                    </TableCell>
                    <TableCell>{expense.invoice_number || '-'}</TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(expense.total_amount, expense.currency || 'GHS')}
                    </TableCell>
                    <TableCell>{getStatusBadge(expense.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {expense.status === 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await ExpenseService.submitExpense(expense.id, user!.id)
                                alert('Expense submitted for approval')
                                loadExpenses()
                              } catch (error) {
                                console.error('Error submitting expense:', error)
                                alert('Failed to submit expense')
                              }
                            }}
                          >
                            Submit
                          </Button>
                        )}
                        {expense.status === 'pending_approval' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedExpense(expense)
                              setShowApprovalDialog(true)
                            }}
                          >
                            Review
                          </Button>
                        )}
                        {(expense.status === 'approved' || expense.status === 'partially_paid') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedExpense(expense)
                              setShowPaymentDialog(true)
                            }}
                          >
                            {expense.status === 'partially_paid' ? 'Pay Balance' : 'Pay'}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Add Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>
              Record a new church expense
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Expense Date */}
              <div className="grid gap-2">
                <Label htmlFor="expense_date">Expense Date *</Label>
                <Input
                  id="expense_date"
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Vendor */}
                <div className="grid gap-2">
                  <Label htmlFor="vendor_id">Vendor *</Label>
                  <Select
                    value={formData.vendor_id}
                    onValueChange={(value) => {
                      const selectedVendor = vendors.find(v => v.id === value)
                      setFormData({
                        ...formData,
                        vendor_id: value,
                        vendor_name: selectedVendor?.vendor_name || ''
                      })
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((vendor) => (
                        <SelectItem key={vendor.id} value={vendor.id}>
                          {vendor.vendor_code} - {vendor.vendor_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Category */}
                <div className="grid gap-2">
                  <Label htmlFor="expense_category">Category *</Label>
                  <Select
                    value={formData.expense_category}
                    onValueChange={(value) => setFormData({ ...formData, expense_category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="supplies">Supplies</SelectItem>
                      <SelectItem value="equipment">Equipment</SelectItem>
                      <SelectItem value="ministry">Ministry</SelectItem>
                      <SelectItem value="payroll">Payroll</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                      <SelectItem value="insurance">Insurance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div className="grid gap-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Amount */}
                <div className="grid gap-2">
                  <Label htmlFor="amount">Amount (GHS) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>

                {/* VAT */}
                <div className="grid gap-2">
                  <Label htmlFor="vat_amount">VAT (GHS)</Label>
                  <Input
                    id="vat_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.vat_amount}
                    onChange={(e) => setFormData({ ...formData, vat_amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>

                {/* Total (calculated) */}
                <div className="grid gap-2">
                  <Label>Total</Label>
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted px-3 text-sm">
                    {formatCurrency(
                      parseFloat(formData.amount || '0') +
                      parseFloat(formData.vat_amount || '0'),
                      'GHS'
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Invoice Number */}
                <div className="grid gap-2">
                  <Label htmlFor="invoice_number">Invoice #</Label>
                  <Input
                    id="invoice_number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                  />
                </div>

                {/* Invoice Date */}
                <div className="grid gap-2">
                  <Label htmlFor="invoice_date">Invoice Date</Label>
                  <Input
                    id="invoice_date"
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                  />
                </div>

                {/* Due Date */}
                <div className="grid gap-2">
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Create Expense
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Expense</DialogTitle>
            <DialogDescription>
              Approve or reject this expense request
            </DialogDescription>
          </DialogHeader>

          {selectedExpense && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vendor:</span>
                  <span className="font-medium">{selectedExpense.vendor_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount:</span>
                  <span className="font-medium">
                    {formatCurrency(selectedExpense.total_amount, selectedExpense.currency || 'GHS')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Description:</span>
                  <span className="font-medium text-right">{selectedExpense.description}</span>
                </div>
              </div>

              <Separator />

              <div className="grid gap-2">
                <Label>Action</Label>
                <Select value={approvalAction} onValueChange={(v: any) => setApprovalAction(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve">Approve</SelectItem>
                    <SelectItem value="reject">Reject</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Comments {approvalAction === 'reject' && '*'}</Label>
                <Textarea
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  rows={3}
                  placeholder={approvalAction === 'reject' ? 'Please provide a reason...' : 'Optional comments'}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApproval}
              variant={approvalAction === 'reject' ? 'destructive' : 'default'}
            >
              {approvalAction === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Record payment to vendor for this expense
            </DialogDescription>
          </DialogHeader>

          {selectedExpense && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vendor:</span>
                  <span className="font-medium">{selectedExpense.vendor_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Amount to Pay:</span>
                  <span className="font-medium text-lg">
                    {formatCurrency(selectedExpense.total_amount, selectedExpense.currency || 'GHS')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Description:</span>
                  <span className="font-medium text-right text-xs">{selectedExpense.description}</span>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="payment_date">Payment Date *</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={paymentData.payment_date}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="payment_amount">Payment Amount (GHS) *</Label>
                  <Input
                    id="payment_amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={selectedExpense.total_amount - (selectedExpense.amount_paid || 0)}
                    value={paymentData.payment_amount}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_amount: e.target.value })}
                    placeholder={formatCurrency(selectedExpense.total_amount - (selectedExpense.amount_paid || 0), 'GHS')}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Balance due: {formatCurrency(selectedExpense.total_amount - (selectedExpense.amount_paid || 0), 'GHS')}
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="payment_method">Payment Method *</Label>
                  <Select
                    value={paymentData.payment_method}
                    onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
                  >
                    <SelectTrigger id="payment_method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="payment_reference">Payment Reference *</Label>
                  <Input
                    id="payment_reference"
                    value={paymentData.payment_reference}
                    onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
                    placeholder="Transaction ID, check number, etc."
                    required
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <p className="text-blue-900">
                  <strong>What happens next:</strong>
                </p>
                <ul className="list-disc list-inside text-blue-800 mt-1 space-y-1">
                  <li>Vendor balance will be reduced by payment amount</li>
                  <li>Journal entry will be posted (DR A/P, CR Cash)</li>
                  <li>
                    {paymentData.payment_amount &&
                     parseFloat(paymentData.payment_amount) < (selectedExpense.total_amount - (selectedExpense.amount_paid || 0))
                      ? 'Expense status will change to "Partially Paid"'
                      : 'Expense status will change to "Paid"'}
                  </li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={!paymentData.payment_reference.trim() || !paymentData.payment_amount || parseFloat(paymentData.payment_amount) <= 0}
            >
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
